import { movies } from "@/mocks/movies";
import { tvSeries } from "@/mocks/tvseries";
import type { Work } from "@/types/film";

export const allWorks: Work[] = [
  ...(movies as unknown as Work[]),
  ...(tvSeries as unknown as Work[]),
];

export function getWorkById(id: string): Work | undefined {
  return allWorks.find((work) => work.id === id);
}

export function getWorksByCategory(category: "movie" | "tv"): Work[] {
  return allWorks.filter((work) => work.category === category);
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}

export function getGenres(works: Work[]): string[] {
  return unique(works.flatMap((work) => work.genres));
}

export function getRegions(works: Work[]): string[] {
  return unique(works.map((work) => work.region));
}

export function getYears(works: Work[]): number[] {
  return unique(works.map((work) => String(work.year)))
    .map((year) => Number(year))
    .sort((a, b) => b - a);
}

export const SORT_OPTIONS = [
  { value: "latest", label: "最新上映" },
  { value: "hot", label: "最热门" },
  { value: "rating", label: "评分最高" },
] as const;

export const EPISODE_OPTIONS = [
  { value: "all", label: "全部集数" },
  { value: "short", label: "1-20 集" },
  { value: "medium", label: "21-40 集" },
  { value: "long", label: "40 集以上" },
] as const;

export interface FilterState {
  genre: string;
  region: string;
  year: string;
  episodes: string;
  sort: string;
}

export const defaultFilter: FilterState = {
  genre: "all",
  region: "all",
  year: "all",
  episodes: "all",
  sort: "latest",
};

export function filterWorks(works: Work[], filter: FilterState): Work[] {
  return works.filter((work) => {
    if (filter.genre !== "all" && !work.genres.includes(filter.genre)) return false;
    if (filter.region !== "all" && work.region !== filter.region) return false;
    if (filter.year !== "all" && String(work.year) !== filter.year) return false;
    if (filter.episodes !== "all") {
      const count = work.episodes ?? 0;
      if (filter.episodes === "short" && (count < 1 || count > 20)) return false;
      if (filter.episodes === "medium" && (count < 21 || count > 40)) return false;
      if (filter.episodes === "long" && count <= 40) return false;
    }
    return true;
  });
}

export function sortWorks(works: Work[], sort: string): Work[] {
  const list = [...works];
  if (sort === "hot") return list.sort((a, b) => b.hot - a.hot);
  if (sort === "rating") return list.sort((a, b) => b.rating - a.rating);
  return list.sort((a, b) => b.year - a.year || b.hot - a.hot);
}

export function searchWorks(keyword: string): Work[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allWorks.filter((work) => {
    const haystack = [
      work.title,
      ...work.genres,
      ...work.tags,
      work.region,
      work.director,
      ...work.actors,
      work.synopsis,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelated(work: Work, limit = 6): Work[] {
  return allWorks
    .filter((item) => item.id !== work.id && item.category === work.category)
    .map((item) => {
      const overlap = item.genres.filter((genre) => work.genres.includes(genre)).length;
      const tagOverlap = item.tags.filter((tag) => work.tags.includes(tag)).length;
      return { item, score: overlap * 2 + tagOverlap + item.hot / 100 };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getFeatured(works: Work[], limit = 6): Work[] {
  return [...works].sort((a, b) => b.hot - a.hot).slice(0, limit);
}

export function getLatest(works: Work[], limit = 6): Work[] {
  return [...works].sort((a, b) => b.year - a.year || b.hot - a.hot).slice(0, limit);
}